import { GameHistory, withOlderHistory } from "../api/gameHistory";
import { applyStreamEvent, GameStreamEvent } from "../api/gameStream";
import { TeamSide } from "../constants/teams";
import { Activity, GameFullResponse, LineUpPlayer } from "../types/gameFull";
import { GameRosters } from "../types/roster";
import { applyLineupDraft, LineupDraft } from "../utils/lineupDraft";
import {
  applyGameStateEdit,
  applyTagOverrides,
  GameStateEdit,
  PitchTags,
} from "../utils/pitchTagging";
import {
  applyInningMoves,
  applyPlateAppearanceEdit,
  applyPlateAppearancePlayers,
  InningMove,
  PlateAppearanceEditValues,
  PlateAppearancePlayers,
} from "../utils/plateAppearanceEditor";
import {
  doesTagUpdateGameState,
  getActivePlateAppearance,
  getActivitiesForPA,
  getDefaultSelectionId,
  getNewestActivity,
  getWaitingPitch,
  WAITING_PITCH_ID,
} from "../utils/plateAppearanceFeed";
import {
  addRosterPlayers,
  setActiveHitter,
  substitutePlayer,
} from "../utils/playerChange";
import {
  applyPlayerEdit,
  applyPlayerEditToPlayers,
  PlayerEdit,
  setPlayerJerseyNumber,
} from "../utils/playerEdit";

/** State and transitions behind useScorekeepingGame. */

export type ScorekeepingState = {
  data: GameFullResponse;
  /** Team rosters: the omnibus rosters plus optimistic local adds */
  rosters: GameRosters;
  /** Activity id, or WAITING_PITCH_ID for the placeholder row */
  selectedId?: string;
  /** PA open in the at-bat editor (ended or active); exclusive with selectedId */
  selectedPlateAppearanceId?: string;
  /** eventRevision of the last game-updated applied, to drop stale ones */
  gameRevision?: number;
  /** PAs moved to another inning in the at-bat editor (frontend only) */
  inningMoves: Record<string, InningMove>;
  /** Pitches and at bats older than the snapshot's are merged in */
  isHistoryLoaded: boolean;
};

export type ScorekeepingAction =
  | { type: "hydrate"; data: GameFullResponse }
  | { type: "mergeHistory"; history: GameHistory }
  | { type: "streamEvent"; event: GameStreamEvent }
  | { type: "select"; id: string }
  | { type: "selectPlateAppearance"; plateAppearanceId: string }
  | {
      type: "editPlateAppearance";
      plateAppearanceId: string;
      values: PlateAppearanceEditValues;
    }
  | {
      type: "saveTags";
      activityId: string;
      tags: PitchTags;
    }
  | {
      type: "revertTags";
      /** The activity as it was before the failed save */
      previous: Activity;
    }
  | { type: "editGameState"; edit: GameStateEdit }
  | { type: "editPlayer"; playerId: string; edit: PlayerEdit }
  | { type: "setJerseyNumber"; playerId: string; jerseyNumber: string }
  | { type: "applyLineups"; draft: LineupDraft }
  | {
      type: "substitutePlayer";
      side: TeamSide;
      outPlayerId: string;
      inPlayer: LineUpPlayer;
      fallback?: Pick<LineUpPlayer, "battingOrder" | "position">;
    }
  | { type: "addRosterPlayers"; side: TeamSide; players: LineUpPlayer[] }
  | { type: "setRosters"; rosters: GameRosters }
  | {
      type: "setPlateAppearancePlayers";
      plateAppearanceId: string;
      players: PlateAppearancePlayers;
    }
  | { type: "setActiveHitter"; player: LineUpPlayer };

/** Next pitch to tag after `eventTime` in the active PA, else the waiting row. */
const getNextSelectionId = (data: GameFullResponse, eventTime: number) => {
  const pa = getActivePlateAppearance(data);
  const nextPitch =
    pa &&
    getActivitiesForPA(data, pa.plateAppearanceId).find(
      (activity) => activity.pitch.eventTime > eventTime,
    );
  if (nextPitch) return nextPitch.activityId;
  return getWaitingPitch(data) ? WAITING_PITCH_ID : undefined;
};

export const scorekeepingReducer = (
  state: ScorekeepingState,
  action: ScorekeepingAction,
): ScorekeepingState => {
  switch (action.type) {
    case "hydrate": {
      // Keep the selection if it's still in the game, else start fresh
      const hasActivity =
        state.selectedId === WAITING_PITCH_ID ||
        action.data.activities.some((a) => a.activityId === state.selectedId);
      const hasPlateAppearance = action.data.plateAppearances.some(
        (pa) => pa.plateAppearanceId === state.selectedPlateAppearanceId,
      );
      const isSameGame = action.data.game.gameId === state.data.game.gameId;
      const inningMoves = isSameGame ? state.inningMoves : {};
      // A re-read snapshot (reconnect) is only the newest page: keep the
      // older history already loaded
      const data =
        isSameGame && state.isHistoryLoaded
          ? withOlderHistory(action.data, {
              activities: state.data.activities,
              plateAppearances: state.data.plateAppearances.filter(
                (pa) => !pa.isPending,
              ),
            })
          : action.data;
      return {
        ...state,
        data: applyInningMoves(data, inningMoves),
        isHistoryLoaded: isSameGame && state.isHistoryLoaded,
        inningMoves,
        selectedId: hasActivity
          ? state.selectedId
          : hasPlateAppearance
            ? undefined
            : getDefaultSelectionId(action.data),
        selectedPlateAppearanceId: hasPlateAppearance
          ? state.selectedPlateAppearanceId
          : undefined,
        gameRevision: isSameGame ? state.gameRevision : undefined,
      };
    }

    case "mergeHistory":
      return {
        ...state,
        data: applyInningMoves(
          withOlderHistory(state.data, action.history),
          state.inningMoves,
        ),
        isHistoryLoaded: true,
      };

    case "streamEvent": {
      const { event } = action;
      if (event.type === "game-updated") {
        const revision = event.state.eventRevision;
        if (
          revision != null &&
          state.gameRevision != null &&
          revision <= state.gameRevision
        ) {
          return state;
        }
        return {
          ...state,
          data: applyStreamEvent(state.data, event),
          gameRevision: revision ?? state.gameRevision,
        };
      }

      const data = applyInningMoves(
        applyStreamEvent(state.data, event),
        state.inningMoves,
      );
      const isNewActivity =
        "activity" in event &&
        data.activities.length > state.data.activities.length;
      if (!isNewActivity) return { ...state, data };

      // A new pitch takes over from the waiting row, no selection, or the
      // newest pitch once it's tagged (e.g. the one that ended the last at
      // bat); selections of older pitches stay put
      const previous = getNewestActivity(state.data.activities);
      const followsNewPitch =
        !state.selectedPlateAppearanceId &&
        (state.selectedId === undefined ||
          state.selectedId === WAITING_PITCH_ID ||
          (state.selectedId === previous?.activityId && !!previous.result));
      return {
        ...state,
        data,
        selectedId: followsNewPitch
          ? event.activity.activityId
          : state.selectedId,
      };
    }

    case "select":
      return {
        ...state,
        selectedId: action.id,
        selectedPlateAppearanceId: undefined,
      };

    case "selectPlateAppearance":
      return {
        ...state,
        selectedId: undefined,
        selectedPlateAppearanceId: action.plateAppearanceId,
      };

    case "editPlateAppearance":
      return {
        ...state,
        data: applyPlateAppearanceEdit(
          state.data,
          action.plateAppearanceId,
          action.values,
        ),
        inningMoves: {
          ...state.inningMoves,
          [action.plateAppearanceId]: {
            inning: action.values.inning,
            isTopOfInning: action.values.isTopOfInning,
          },
        },
      };

    case "saveTags": {
      const updatesGameState = doesTagUpdateGameState(
        state.data,
        action.activityId,
      );
      const eventTime = state.data.activities.find(
        (a) => a.activityId === action.activityId,
      )?.pitch.eventTime;
      // The game service ends the at bat and starts the next
      const data = applyTagOverrides(state.data, {
        [action.activityId]: action.tags,
      });
      // Moves on to the next pitch to tag (or the waiting row); edits to
      // older pitches keep the selection
      const selectedId =
        updatesGameState && eventTime != null
          ? (getNextSelectionId(data, eventTime) ?? state.selectedId)
          : state.selectedId;
      return { ...state, data, selectedId };
    }

    case "revertTags": {
      // Only the tags: the service may have updated the pitch since
      const { activityId, result, resultingState } = action.previous;
      return {
        ...state,
        data: {
          ...state.data,
          activities: state.data.activities.map((activity) =>
            activity.activityId === activityId
              ? { ...activity, result, resultingState }
              : activity,
          ),
        },
      };
    }

    case "editGameState":
      return { ...state, data: applyGameStateEdit(state.data, action.edit) };

    case "editPlayer": {
      const editRoster = (side: keyof GameRosters) => ({
        ...state.rosters[side],
        players: applyPlayerEditToPlayers(
          state.rosters[side].players,
          action.playerId,
          action.edit,
        ),
      });
      return {
        ...state,
        data: applyPlayerEdit(state.data, action.playerId, action.edit),
        rosters: { away: editRoster("away"), home: editRoster("home") },
      };
    }

    case "setJerseyNumber": {
      const { playerId, jerseyNumber } = action;
      const set = (players: LineUpPlayer[]) =>
        setPlayerJerseyNumber(players, playerId, jerseyNumber);
      const { lineUp } = state.data;
      return {
        ...state,
        data: {
          ...state.data,
          lineUp: {
            home: set(lineUp.home),
            away: set(lineUp.away),
            extra: set(lineUp.extra),
          },
        },
        rosters: {
          away: {
            ...state.rosters.away,
            players: set(state.rosters.away.players),
          },
          home: {
            ...state.rosters.home,
            players: set(state.rosters.home.players),
          },
        },
      };
    }

    case "applyLineups":
      return {
        ...state,
        ...applyLineupDraft(state.data, state.rosters, action.draft),
      };

    case "substitutePlayer":
      return {
        ...state,
        ...substitutePlayer(
          state.data,
          state.rosters,
          action.side,
          action.outPlayerId,
          action.inPlayer,
          action.fallback,
        ),
      };

    case "addRosterPlayers":
      return {
        ...state,
        rosters: addRosterPlayers(state.rosters, action.side, action.players),
      };

    case "setRosters":
      return { ...state, rosters: action.rosters };

    case "setPlateAppearancePlayers":
      return {
        ...state,
        data: applyPlateAppearancePlayers(
          state.data,
          action.plateAppearanceId,
          action.players,
        ),
      };

    case "setActiveHitter":
      return {
        ...state,
        data: setActiveHitter(state.data, action.player),
      };

    default:
      return state;
  }
};
